import type { ComponentType, CSSProperties, ReactNode } from "react"
import { useMemo, useState } from "react"

import type { Request } from "@productlane/api/src/lib/support-portal/request.types"
import {
	getRequestDisplayDate,
	getRequestStatusLabel,
	groupRequestsByStatus,
	REQUEST_STATUS_LABELS,
	REQUEST_STATUS_ORDER,
} from "@productlane/lib/src/support-requests"

import { RequestListItem } from "./request-list-item"
import { RequestsListWrapper } from "./requests-list-wrapper"

export {
	getRequestStatusKey,
	isHiddenRequestStatus,
} from "@productlane/lib/src/support-requests"
export const STATUS_ORDER = REQUEST_STATUS_ORDER
export const STATUS_LABELS = REQUEST_STATUS_LABELS

type ProjectIconResolver = (
	icon: string | null | undefined,
) => ComponentType<{ className?: string; style?: CSSProperties }>

type KeyboardNavHook = (args: {
	itemCount: number
	onItemSelect: (index: number) => void
	modalOpen?: boolean
	enabled?: boolean
}) => { focusedIndex: number; setFocusedIndex: (index: number) => void }

const noopKeyboardNav: KeyboardNavHook = () => ({
	focusedIndex: -1,
	setFocusedIndex: () => undefined,
})

interface Props {
	requests: Request[]
	userEmail?: string
	onRequestClick: (request: Request) => void
	getProjectIcon: ProjectIconResolver
	isDueDateEnabled?: boolean
	t?: (key: string, fallback: string) => string
	/** Injected priority control per row. Omit for a read-only list. */
	renderPriority?: (request: Request) => ReactNode
	/** Injected keyboard-navigation hook. Omit to disable keyboard nav. */
	useKeyboardNav?: KeyboardNavHook
	/** Pointer cursor on rows. Off in the dashboard app per house style. */
	showPointer?: boolean
}

export function RequestsCard({
	requests,
	userEmail,
	onRequestClick,
	getProjectIcon,
	isDueDateEnabled = false,
	t,
	renderPriority,
	useKeyboardNav = noopKeyboardNav,
	showPointer = true,
}: Props) {
	const isUserRequest = useMemo(
		() =>
			(request: Request): boolean =>
				userEmail ? request.contact?.email === userEmail : false,
		[userEmail],
	)

	const getRequestDate = useMemo(
		() =>
			(request: Request): number => {
				const dateValue = getRequestDisplayDate(request)
				if (!dateValue) return 0
				return new Date(dateValue).getTime()
			},
		[],
	)

	const compareRequests = useMemo(
		() =>
			(a: Request, b: Request): number => {
				const aImportant = a.category === "important"
				const bImportant = b.category === "important"
				if (aImportant && !bImportant) return -1
				if (!aImportant && bImportant) return 1
				const aIsUser = isUserRequest(a)
				const bIsUser = isUserRequest(b)
				if (aIsUser && !bIsUser) return -1
				if (!aIsUser && bIsUser) return 1
				return getRequestDate(b) - getRequestDate(a)
			},
		[isUserRequest, getRequestDate],
	)

	const groupedRequests = useMemo(
		() => groupRequestsByStatus(requests, compareRequests),
		[requests, compareRequests],
	)

	// "completed" starts collapsed; users expand it on demand.
	const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
		completed: true,
	})

	const allRequests = useMemo(
		() => groupedRequests.flatMap((g) => g.requests),
		[groupedRequests],
	)

	const handleClick = (request: Request) => {
		onRequestClick(request)
	}

	const { focusedIndex, setFocusedIndex } = useKeyboardNav({
		itemCount: allRequests.length,
		onItemSelect: (index) => {
			const request = allRequests[index]
			if (request) {
				handleClick(request)
			}
		},
		modalOpen: false,
		enabled: true,
	})

	if (!requests || requests.length === 0) {
		return null
	}

	return (
		<div className="flex flex-col gap-4">
			{groupedRequests.map(({ status, statusType, requests: groupReqs }) => (
				<RequestsListWrapper
					key={status}
					title={getRequestStatusLabel(status, t)}
					statusType={statusType}
					count={groupReqs.length}
					card
					collapsible
					collapsed={collapsed[status] ?? false}
					onToggle={() =>
						setCollapsed((prev) => ({ ...prev, [status]: !prev[status] }))
					}
				>
					{groupReqs.map((request, index) => {
						const itemIndex = allRequests.findIndex(
							(r) => r.item.id === request.item.id,
						)
						return (
							<RequestListItem
								key={`${status}-${index}`}
								onClick={() => handleClick(request)}
								request={request}
								isFocused={itemIndex === focusedIndex}
								onMouseEnter={() => setFocusedIndex(itemIndex)}
								onMouseLeave={() => setFocusedIndex(-1)}
								isDueDateEnabled={isDueDateEnabled}
								getProjectIcon={getProjectIcon}
								t={t}
								renderPriority={renderPriority}
								showPointer={showPointer}
							/>
						)
					})}
				</RequestsListWrapper>
			))}
		</div>
	)
}
